import { INVOICE_STATUSES } from "constants/invoicing";
import { REVENUE_DATA_SOURCE } from "constants/revenueDataSource";
import { useGetAllInvoicesQuery } from "store/apis/invoices";

interface UseInvoiceBulkActionProps {
  groupId: string;
  entityId: string;
  selectedRows: string[];
  newSelectedRows?: string[];
  excludeStripeInvoices?: boolean;
}

export const useInvoiceBulkAction = ({
  groupId,
  entityId,
  selectedRows,
  newSelectedRows,
  excludeStripeInvoices = true,
}: UseInvoiceBulkActionProps) => {
  const { data: allInvoices, isLoading } = useGetAllInvoicesQuery(
    {
      groupId,
      entityId,
      searchTerm: "",
      limit: selectedRows.length || 0,
      invoiceIds: selectedRows.join(",") || "",
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const { invoices = [] } = allInvoices || {};

  const valid = invoices.filter((row) => {
    const isSelected = newSelectedRows?.includes(row.uuid);
    const isDraft = row.status === INVOICE_STATUSES.DRAFT;
    const isNotStripe = row.source !== REVENUE_DATA_SOURCE.STRIPE;

    if (excludeStripeInvoices) {
      return isDraft && isNotStripe && isSelected;
    }
    return isDraft && isSelected;
  });

  const invalid = invoices.filter((row) => {
    const isNotStripe = row.source !== REVENUE_DATA_SOURCE.STRIPE;

    if (excludeStripeInvoices) {
      return row.status !== INVOICE_STATUSES.DRAFT || !isNotStripe;
    }
    return row.status !== INVOICE_STATUSES.DRAFT;
  });

  const selectedInvoices = invoices.filter((row) =>
    newSelectedRows?.includes(row.uuid)
  );

  const hasDueOrOverdueInSelection = selectedInvoices.some(
    (row) =>
      row.status === INVOICE_STATUSES.DUE ||
      row.status === INVOICE_STATUSES.OVERDUE
  );

  const hasDraftInSelection = valid.length > 0;

  const hasOnlyDraftAndDueOverdue = selectedInvoices.every(
    (row) =>
      row.status === INVOICE_STATUSES.DRAFT ||
      row.status === INVOICE_STATUSES.DUE ||
      row.status === INVOICE_STATUSES.OVERDUE
  );

  const hasNoStripeInvoices = selectedInvoices.every(
    (row) => row.source !== REVENUE_DATA_SOURCE.STRIPE
  );

  const shouldShowWarningModal =
    hasDueOrOverdueInSelection &&
    hasDraftInSelection &&
    hasOnlyDraftAndDueOverdue &&
    hasNoStripeInvoices;

  const totalInvoices = invoices.length;
  const isAllInvoiceInvalid = valid.length === 0;
  const shouldShowButton = valid.length > 0 && newSelectedRows?.length !== 0;

  return {
    allInvoices,
    invoices,
    isLoading,
    newSelectedRows,
    valid,
    invalid,
    totalInvoices,
    isAllInvoiceInvalid,
    shouldShowButton,
    hasDueOrOverdueInSelection,
    shouldShowWarningModal,
  };
};
